import HttpTransport from '@/bundles/Http/transport/HttpTransport';
import ErrorMapper from '@/bundles/Http/mappers/ErrorMapper';
import MemberToMapper from '@/bundles/Members/mappers/MemberToMapper';
import Member from '@/bundles/Members/models/Member';
import { PaginationResponse } from '@/types/PagintaionResponse';
import { generateParams } from '@/utils/helpers';
import CreateUserDto from '@/bundles/Members/dto/create-user';
import { IBufferResponse, IExportPayload } from '@/bundles/App/interfaces/IExport';
import { isValuePresent } from '@/bundles/App/helpers/isValuePresent';
import { OfficeLocation } from '@/bundles/OfficeLocation/models/OfficeLocation';
import { Nullable } from '@/utils/types';
import { AiKeyProviderEnum } from '@/bundles/Members/enums';
import { IAiKeyListItem } from '@/bundles/Members/interfaces/IMemberSettings';

export default class MemberService {
  private readonly transport: HttpTransport;
  private readonly errorMapper: ErrorMapper;
  private readonly memberMapper: MemberToMapper;

  constructor ({
    transport,
    errorMapper,
    memberMapper,
  }: {
    transport: HttpTransport;
    errorMapper: ErrorMapper;
    memberMapper: MemberToMapper;
  }) {
    this.transport = transport;
    this.errorMapper = errorMapper;
    this.memberMapper = memberMapper;
  }

  async list (params: any[], { controller }: { controller?: Nullable<AbortController> } = {}): Promise<PaginationResponse<Member>> {
    try {
      const paramsString = generateParams(params);
      const { data, total } = await this.transport.get(`/user${ paramsString }`, {}, controller);
      return {
        data: data.map((rawData: any) => this.memberMapper.mapToModel(rawData)),
        total,
      };
    } catch (error) {
      throw this.errorMapper.mapToCodeModel(error);
    }
  }

  async listGroupFilters (params: Record<string, any>, { controller }: { controller?: Nullable<AbortController> } = {}): Promise<PaginationResponse<Member>> {
    try {
      const { data, total } = await this.transport.post(`/user/list`, params, controller);
      return {
        data: data.map((rawData: any) => this.memberMapper.mapToModel(rawData)),
        total,
      };
    } catch (error) {
      throw this.errorMapper.mapToCodeModel(error);
    }
  }

  async create (payload: CreateUserDto): Promise<Member> {
    try {
      const response = await this.transport.post('/user', payload);
      return this.memberMapper.mapToModel(response);
    } catch (error) {
      throw this.errorMapper.mapToCodeModel(error);
    }
  }

  async getOne (id: number, options = {}): Promise<Member> {
    try {
      const response = await this.transport.get(`/user/${ id }`, {}, null, { options });
      return this.memberMapper.mapToModel(response);
    } catch (error) {
      throw this.errorMapper.mapToCodeModel(error);
    }
  }

  async getMe (options = {}): Promise<Member> {
    try {
      const response = await this.transport.get(`/user/info/me`, {}, null, { options });
      return this.memberMapper.mapToModel(response);
    } catch (error) {
      throw this.errorMapper.mapToCodeModel(error);
    }
  }

  async sendResetLinkToUser (id: number): Promise<void> {
    try {
      return await this.transport.patch(`/user/${ id }/reset-password`);
    } catch (error) {
      throw this.errorMapper.mapToCodeModel(error);
    }
  }

  async changeUserEmail (id: number, payload: { email: string }): Promise<void> {
    try {
      return await this.transport.patch(`/user/${ id }/email`, payload);
    } catch (error) {
      throw this.errorMapper.mapToCodeModel(error);
    }
  }

  async toggleActiveLogin (id: number, payload: { active_login: boolean }): Promise<void> {
    try {
      return await this.transport.patch(`/user/${ id }/active-login`, payload);
    } catch (error) {
      throw this.errorMapper.mapToCodeModel(error);
    }
  }

  async changeUserRoles (id: number, payload: { roles: number[] }): Promise<Member> {
    try {
      const response = await this.transport.patch(`/user/${ id }/roles`, payload);
      return this.memberMapper.mapToModel(response);
    } catch (error) {
      throw this.errorMapper.mapToCodeModel(error);
    }
  }

  async changeUserMcpRoles (id: number, payload: { mcpRoles: number[] }): Promise<Member> {
    try {
      const response = await this.transport.patch(`/user/${ id }/mcp-roles`, payload);
      return this.memberMapper.mapToModel(response);
    } catch (error) {
      throw this.errorMapper.mapToCodeModel(error);
    }
  }

  async archive ({ ids = [] }: { ids: number[] }): Promise<any> {
    try {
      return await this.transport.patch('/user/archive', { ids });
    } catch (error) {
      throw this.errorMapper.mapToCodeModel(error);
    }
  }

  async getTeamMembersForUser (id: number, params: Record<string, string|boolean> = {}): Promise<Member[]> {
    try {
      const response = await this.transport.get(`/user/${ id }/team`, params);
      return response.map((record: any) => this.memberMapper.mapToModel(record));
    } catch (error) {
      throw this.errorMapper.mapToCodeModel(error);
    }
  }

  async exportToExcel (payload: IExportPayload): Promise<IBufferResponse> {
    try {
      return await this.transport.post(`/user/export-members/`, payload);
    } catch (error) {
      throw this.errorMapper.mapToCodeModel(error);
    }
  }

  async getMemberPrimaryLocation (id: number): Promise<Nullable<OfficeLocation>> {
    try {
      const response = await this.transport.get(`/user/${ id }/primary-location`);

      if (isValuePresent(response)) {
        return new OfficeLocation(response);
      }

      return null;
    } catch (error) {
      throw this.errorMapper.mapToCodeModel(error);
    }
  }

  async restore (id: number): Promise<void> {
    try {
      await this.transport.patch(`/user/${ id }/restore`, null);
    } catch (error) {
      throw this.errorMapper.mapToCodeModel(error);
    }
  }

  // `memberId` is the member (user) id, matching GET /member-settings/:id on the v1 API
  async getAiKeys (memberId: number): Promise<IAiKeyListItem[]> {
    try {
      return await this.transport.get(`/member-settings/${ memberId }/ai-keys`);
    } catch (error) {
      throw this.errorMapper.mapToCodeModel(error);
    }
  }

  // `settingsKey` is the MemberSettings `_key`, matching the other PATCH /member-settings/:id/* routes
  async upsertAiKey (settingsKey: string, payload: { provider: AiKeyProviderEnum, key: string }): Promise<IAiKeyListItem[]> {
    try {
      return await this.transport.patch(`/member-settings/${ settingsKey }/ai-keys`, payload);
    } catch (error) {
      throw this.errorMapper.mapToCodeModel(error);
    }
  }

  // `memberId` is the member (user) id, same as getAiKeys
  async deleteAiKey (memberId: number, provider: AiKeyProviderEnum): Promise<IAiKeyListItem[]> {
    try {
      return await this.transport.delete(`/member-settings/${ memberId }/ai-keys/${ provider }`);
    } catch (error) {
      throw this.errorMapper.mapToCodeModel(error);
    }
  }
}
